type LogoutHandler = () => void;

interface ApiErrorResponse {
  message: string | string[];
  error: string;
  statusCode: number;
}

interface HttpError {
  response?: {
    data?: ApiErrorResponse;
    status?: number;
    statusText?: string;
  };
  message?: string;
  statusText?: string;
}

let logoutHandler: LogoutHandler | null = null;

export const setLogoutHandler = (handler: LogoutHandler) => {
  logoutHandler = handler;
};

const toMessageString = (value: unknown): string => {
  if (typeof value === "string") {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => toMessageString(item)).filter(Boolean).join(", ");
  }
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value as string | number | boolean | bigint | symbol);
};

export const extractErrorMessage = (error: HttpError): string => {
  if (error?.response?.data) {
    const apiError = error.response.data as ApiErrorResponse;
    const apiMessage = toMessageString(apiError.message);
    if (apiMessage) {
      return apiMessage;
    }

    const apiErrorText = toMessageString(apiError.error);
    if (apiErrorText) {
      return apiErrorText;
    }
  }

  const errorMessage = toMessageString(error?.message);
  if (errorMessage) {
    return errorMessage;
  }

  const statusText = toMessageString(error?.response?.statusText);
  if (statusText) {
    return statusText;
  }

  return "Bir hata oluştu";
};

const hasStatus401 = (error: HttpError): boolean => {
  // Check API response status code
  if (error?.response?.data?.statusCode === 401) {
    return true;
  }

  // Check HTTP response status
  if (error?.response?.status === 401) {
    return true;
  }

  return false;
};

export const is401Error = (error: HttpError): boolean => {
  if (hasStatus401(error)) {
    return true;
  }

  const errorMessage = extractErrorMessage(error);
  const statusText = error?.statusText || "";

  return (
    errorMessage.startsWith("401:") ||
    statusText === "Unauthorized" ||
    errorMessage.includes("Unauthorized") ||
    // HTTP status patterns - only when clearly indicating HTTP status
    /\bstatus:?\s*401\b/i.test(errorMessage) ||
    /\bhttp\s+401\b/i.test(errorMessage) ||
    /\b401\s+(unauthorized|error|status)\b/i.test(errorMessage) ||
    // Authentication-specific error messages
    errorMessage.includes("Token is invalid or expired") ||
    errorMessage.includes("Invalid credentials") ||
    // API endpoint specific auth failures - but only for auth-related endpoints
    (/\blogin\s+failed\b/i.test(errorMessage) &&
      /\bauth|login|credential/i.test(errorMessage)) ||
    (/\brefresh\s+failed\b/i.test(errorMessage) &&
      /\btoken|auth/i.test(errorMessage)) ||
    /\bfetching\s+me\s+failed\b/i.test(errorMessage)
  );
};

import { getRefreshedToken } from "./tokenRefresh";

export const handleError = async (error: HttpError) => {
  if (is401Error(error)) {
    // Try refreshing the token before logging out
    const newToken = await getRefreshedToken();
    if (newToken) {
      return; // Refresh succeeded, don't logout
    }

    if (logoutHandler) {
      logoutHandler();
    }
  }
};

export const globalErrorHandler = {
  setLogoutHandler,
  handleError,
  is401Error,
  extractErrorMessage,
  hasStatus401,
};
